import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import type {
  QuoteItemDTO,
  QuoteProductDTO
} from '@setvi/shared/services/react-query';
// eslint-disable-next-line import/no-extraneous-dependencies
import { generateKeyBetween } from 'fractional-indexing';
import {
  addQuoteV2FreehandMutation,
  moveQuoteV2ItemsMutation
} from 'Services/Mutations/QuotesV2';
import type { AddQuoteV2FreehandResponse } from 'Services/Query/QuotesV2/Types';
import {
  getLastPositionInContainer,
  calculateGrossProfitPercentage
} from '../utils';
import {
  updateItemInTree,
  getInsertPositionsAfter,
  sortByPosition
} from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

export interface AddFreehandVariables {
  name: string;
  sku: string;
  sell: number;
  cost: number;
  vendorName: string;
  sectionId?: string;
  note?: string;
  unitOfMeasure?: string;
  packSize?: string;
  /**
   * When set, the SPO item is inserted directly after this item (in whichever
   * container it lives in) instead of appended. The container is resolved from
   * the anchor, so `sectionId` is ignored while this is present.
   */
  afterItemId?: string | null;
}

/**
 * Resolve the target container `sectionId` and the `position` key for a
 * freehand item: inserted after `afterItemId` when provided, otherwise
 * appended to the end of the `sectionId` container.
 */
const resolveFreehandPlacement = (
  items: QuoteItemDTO[],
  sectionId: string,
  afterItemId?: string | null
): { sectionId: string; position: string } => {
  if (afterItemId) {
    const insert = getInsertPositionsAfter(items, afterItemId, 1);
    if (insert) {
      return {
        sectionId: insert.sectionId || '',
        position: insert.positions[0]
      };
    }
  }

  const lastPos = getLastPositionInContainer(items, sectionId || null);
  return { sectionId, position: generateKeyBetween(lastPos, null) };
};

const buildOptimisticItem = (
  vars: AddFreehandVariables,
  tempId: string
): QuoteProductDTO => {
  const {
    name,
    sku,
    sell,
    cost,
    vendorName,
    sectionId,
    note,
    unitOfMeasure,
    packSize
  } = vars;

  return {
    _id: tempId,
    id: tempId,
    type: 'product',
    productId: '',
    name,
    sku,
    quantity: 1,
    sell,
    cost,
    note: note || null,
    vendor: { name: vendorName, _id: null, vendorId: null },
    images: null,
    imageUrl: '',
    isFreeHand: true,
    isEditable: true,
    labelType: '',
    customLabel: '',
    includeInTotalCost: false,
    minimumQuoteQuantity: 1,
    overrideMinimumQuoteQuantity: true,
    children: [],
    parentID: sectionId || null,
    totalGrossProfit: sell - cost,
    totalGrossProfitPercentage: calculateGrossProfitPercentage(cost, sell),
    description: '',
    packSize: packSize || '',
    unitOfMeasure: unitOfMeasure || '',
    manufacturerSku: '',
    isPricingFetching: true
  } as unknown as QuoteProductDTO;
};

const addItemOptimistically = (
  items: QuoteItemDTO[],
  newItem: QuoteProductDTO,
  sectionId: string
): QuoteItemDTO[] => {
  if (!sectionId) {
    return sortByPosition([...items, newItem]);
  }

  return items.map(item => {
    if (
      item.type === 'section' &&
      (item.id || (item as any)._id) === sectionId
    ) {
      return {
        ...item,
        children: sortByPosition([...((item as any).children || []), newItem])
      };
    }
    return item;
  });
};

/**
 * Add a freehand (SPO) item to a quote via the dedicated v2 freehand endpoint.
 * Applies an optimistic update immediately, reverts on error, and invalidates
 * on success to sync the real server data.
 */
export const useAddFreehand = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn: createFreehand } = addQuoteV2FreehandMutation();
  const { mutationFn: moveItems } = moveQuoteV2ItemsMutation();

  return useMutation({
    mutationFn: async ({
      name,
      sku,
      sell,
      cost,
      vendorName,
      sectionId: sectionIdArg = '',
      note = '',
      unitOfMeasure = '',
      packSize = '',
      afterItemId
    }: AddFreehandVariables) => {
      const items = cache.getItems() || [];

      // Resolve the target container + where the SPO should end up.
      let sectionId = sectionIdArg;
      let targetPosition: string | null = null;
      if (afterItemId) {
        const insert = getInsertPositionsAfter(items, afterItemId, 1);
        if (insert) {
          sectionId = insert.sectionId || '';
          [targetPosition] = insert.positions;
        }
      }

      // The freehand-create endpoint only accepts an append position, so always
      // create at the end of the target container...
      const appendPosition = generateKeyBetween(
        getLastPositionInContainer(items, sectionId || null),
        null
      );

      const created = (await createFreehand({
        quoteId,
        name,
        sku,
        sell,
        cost,
        quantity: 1,
        position: appendPosition,
        note,
        sectionId,
        vendorName,
        unitOfMeasure,
        packSize
      })) as AddQuoteV2FreehandResponse;

      // ...then move it to the requested insert spot (the move endpoint accepts
      // arbitrary positions, unlike freehand-create). If the move fails the SPO
      // simply stays appended, which a refetch will reflect.
      const newId = created?.item?._id;
      const finalPosition = targetPosition ?? appendPosition;
      if (targetPosition && newId) {
        try {
          await moveItems({
            quoteId,
            moves: [
              {
                itemId: newId,
                targetSectionId: sectionId || null,
                position: targetPosition
              }
            ]
          });
        } catch {
          // Keep the created item; refetch reflects its actual position.
        }
      }

      return {
        ...created,
        __finalPosition: finalPosition,
        __sectionId: sectionId
      };
    },

    onMutate: async (variables: AddFreehandVariables) => {
      await cache.cancel();

      const previousItems = cache.getItems();
      const tempId = uuidv4();

      // Resolve container + position from pre-optimistic data (insert or append)
      const { sectionId, position } = resolveFreehandPlacement(
        previousItems || [],
        variables.sectionId || '',
        variables.afterItemId
      );

      const newItem = {
        // Reflect the resolved container on the optimistic item so it renders
        // under the correct section when inserted after an anchor.
        ...buildOptimisticItem({ ...variables, sectionId }, tempId),
        position
      } as unknown as QuoteProductDTO;

      cache.setItems(old =>
        addItemOptimistically(old || [], newItem, sectionId)
      );

      return { previousItems, tempId, sectionId };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: (data, _variables, context) => {
      const response = data as AddQuoteV2FreehandResponse & {
        __finalPosition?: string;
        __sectionId?: string;
      };
      const serverItem = response?.item;
      if (serverItem?._id && context?.tempId) {
        const sid = response.__sectionId ?? context.sectionId;
        const finalPosition = response.__finalPosition ?? serverItem.position;
        cache.setItems(old => {
          if (!old) return old;
          // Adopt the server id AND the final (post-move) position, then
          // re-sort the affected container so an inserted-between SPO lands at
          // the right spot instead of snapping to the end on refetch.
          const updated = updateItemInTree(old, context.tempId, {
            _id: serverItem._id,
            id: serverItem._id,
            position: finalPosition,
            isPricingFetching: false
          } as any);

          if (!sid) return sortByPosition(updated);
          return updated.map(item =>
            item.type === 'section' && (item.id || (item as any)._id) === sid
              ? {
                  ...item,
                  children: sortByPosition((item as any).children || [])
                }
              : item
          );
        });
      }
      if (response?.totals) {
        cache.setQuote(old =>
          old
            ? {
                ...old,
                totalSell: response.totals.totalSell,
                totalCost: response.totals.totalCost
              }
            : old
        );
      }
      cache.invalidate();
    }
  });
};
